import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import DivContainer from "../../components/layouts/DivContainer";
import Modal from "../../components/Modals/Modal";
import SearchModal from "../../components/Modals/SearchModal";
import PdfPreview from "../../components/Reports/PdfPreview";
import { getPlanPartoDetalle } from "../../api/planParto";
import empresaApi from "../../api/empresaApi";
import usePlanParto from "../../hooks/planParto/usePlanParto";
import { useLocation } from "../../hooks/useLocation";
import { usePrestadores } from "../../hooks/useProvider";
import { filterPrestadores } from "../../api/resultApi";
import useAuth from "../../hooks/useAuth";
import routes from "../../routes/routes";
import { BtnGeneral } from "../../styles/Buttons";
import {
  AmberFieldset,
  AmberLegend,
  AmberNote,
  CheckRow,
  Container,
  Divider,
  Field,
  FooterActions,
  FormPanel,
  Grid,
  H1,
  HeaderCard,
  HeaderTop,
  HistoricoItem,
  HistoricoList,
  HistoryTitle,
  IndigoCheckRow,
  IndigoFieldset,
  IndigoInput,
  IndigoLabel,
  IndigoLegend,
  IndigoSection,
  InnerFieldset,
  InnerFieldsetSolid,
  InnerLegend,
  Input,
  Label,
  LegalBox,
  LegalList,
  LegalTitle,
  Page,
  PatientActions,
  PatientBox,
  PatientGrid,
  Pill,
  RadioColumn,
  RadioRow,
  RedUpper,
  Section,
  SectionDescription,
  SectionTopRow,
  Select,
  SelectAllRow,
  Small,
  Spacer,
  Strong,
  StrongInherit,
  SubTitle,
  Textarea,
  TextareaTall,
  BlockSubtitle,
  TinyCheckRow,
  TinyNote,
  WhiteCompactInput,
  Wrapper,
  Legend,
} from "./PlanParto.style";

const ALARMA_FIELDS = [
  {
    key: "signos_alarma_neurologicos",
    label: "Cefalea intensa, tinnitus (zumbido en oídos), visión borrosa o fosfenos.",
  },
  { key: "signos_alarma_edema", label: "Edema marcado en cara, manos o pies." },
  { key: "signos_alarma_sangrado", label: "Salida de sangre o líquido amniótico por genitales." },
  { key: "signos_alarma_dolor_abdominal", label: "Epigastralgia (dolor en la boca del estómago) o dolor opresivo en el pecho." },
  { key: "signos_alarma_movimientos", label: "Ausencia o disminución extrema de movimientos fetales (menos de 2 horas)." },
  { key: "signos_alarma_contracciones", label: "Dolor pélvico tipo cólico frecuente con endurecimiento sostenido del abdomen." },
];

const KIT_FIELDS = [
  { key: "kit_ingreso_hc_completa", label: "Historia clínica antenatal, laboratorios y ecografías impresas." },
  { key: "kit_ingreso_documentos", label: "Documento de identificación original de la madre." },
  { key: "kit_ingreso_kit_rn", label: "Maleta del recién nacido (Ropa, pañales, cobija, toalla)." },
  { key: "kit_ingreso_kit_materna", label: "Maleta materna (Ropa cómoda y holgada, toallas higiénicas maternas, toalla)." },
  { key: "kit_ingreso_autorizaciones", label: "Autorizaciones o vigencia de derechos con la EPS." },
];

const PlanParto = () => {
  const navigate = useNavigate();
  const { sedeActiva } = useAuth();
  const {
    canSave,
    epsTramite,
    handleSelectPatient,
    historico,
    idPlanParto,
    loadDetalle,
    loadingDetalle,
    loadingHistorico,
    plan,
    resetForm,
    saving,
    selectedPatient,
    setEpsTramite,
    setPlan,
    submit,
  } = usePlanParto();
  const [searchOpen, setSearchOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [showProviderFilters, setShowProviderFilters] = useState(false);
  const [providerDept, setProviderDept] = useState("");
  const [providerTown, setProviderTown] = useState("");
  const [providerTipoEntidad, setProviderTipoEntidad] = useState("");
  const [providerEsEse, setProviderEsEse] = useState("");
  const [providerSearch, setProviderSearch] = useState("");
  const allAlarmasChecked = useMemo(() => ALARMA_FIELDS.every((f) => !!plan[f.key]), [plan]);
  const allKitChecked = useMemo(() => KIT_FIELDS.every((f) => !!plan[f.key]), [plan]);
  const searchPatientFilters = useMemo(() => ({ cohortSubgroupActive: 9 }), []);

  const { departments, towns } = useLocation(showProviderFilters ? providerDept : null, showProviderFilters);
  const prestadores = usePrestadores(
    showProviderFilters ? providerDept : "",
    showProviderFilters ? providerTown : "",
    showProviderFilters ? providerTipoEntidad : "",
    showProviderFilters ? providerEsEse : "",
    !!selectedPatient,
    showProviderFilters ? 1 : 0
  );

  const isLoadingPrestadores = useMemo(() => !!selectedPatient && prestadores.length === 0, [prestadores.length, selectedPatient]);

  const prestadoresOptions = useMemo(() => {
    if (!Array.isArray(prestadores) || prestadores.length === 0) return [];

    const base = providerSearch.trim() ? filterPrestadores(prestadores, providerSearch) : prestadores;

    return base.slice(0, 100).map((p) => ({
      value: p?.CODIGO_HABILITACION,
      label:
        typeof p?.RAZON_SOCIAL === "string" && p.RAZON_SOCIAL.length > 50
          ? p.RAZON_SOCIAL.substring(0, 70) + "..."
          : p?.RAZON_SOCIAL || "",
      raw: p,
      title: p?.RAZON_SOCIAL,
    }));
  }, [prestadores, providerSearch]);

  const selectedPrestadorParto = useMemo(() => {
    const v = plan.prestador_parto_normal || "";
    if (!v) return null;
    return prestadoresOptions.find((o) => o.value === v) || { value: v, label: v };
  }, [plan.prestador_parto_normal, prestadoresOptions]);

  const selectedPrestadorUrgencias = useMemo(() => {
    const v = plan.prestador_urgencias_cesarea || "";
    if (!v) return null;
    return prestadoresOptions.find((o) => o.value === v) || { value: v, label: v };
  }, [plan.prestador_urgencias_cesarea, prestadoresOptions]);

  const printMeta = useMemo(() => ({
    fechaImpresion: new Date().toLocaleString(),
    empresa: sedeActiva?.nombreEmpresa || "",
    nit: sedeActiva?.nit || "",
    sede: sedeActiva?.nombreSede || "",
    codigoSede: sedeActiva?.codigoSede || "",
    logo: sedeActiva?.logoEmpresa || sedeActiva?.logo || null,
  }), [sedeActiva]);

  const loadPrintData = useCallback(async () => {
    if (!idPlanParto) throw new Error("No hay Plan de Parto para imprimir");
    const json = await getPlanPartoDetalle(String(idPlanParto));
    if (!json?.success) throw new Error(json?.message || "No se pudo cargar el Plan de Parto");

    const data = json.data || {};
    const planObj = data.plan || {};

    // Si aún no tiene logo (por ej. sesión antigua), intentar obtenerlo de empresaApi
    if (!planObj.empresa_logo && (sedeActiva?.id_empresa || planObj.id_empresa)) {
      try {
        const empId = String(sedeActiva?.id_empresa || planObj.id_empresa);
        const empresasRes = await empresaApi.getAllActivas();
        const empList = Array.isArray(empresasRes?.data) ? empresasRes.data : Array.isArray(empresasRes) ? empresasRes : [];
        const currentEmp = empList.find((e) => String(e.id) === empId);
        if (currentEmp?.logo) {
          planObj.empresa_logo = currentEmp.logo;
          try {
            const rawSede = sessionStorage.getItem("sedeActiva");
            if (rawSede) {
              const parsed = JSON.parse(rawSede);
              parsed.logoEmpresa = currentEmp.logo;
              sessionStorage.setItem("sedeActiva", JSON.stringify(parsed));
            }
          } catch (_) {}
        }
      } catch (err) {
        console.warn("[PlanParto] No se pudo cargar logo de empresaApi:", err);
      }
    }

    return data;
  }, [idPlanParto, sedeActiva?.id_empresa]);

  useEffect(() => {
    if (selectedPatient) return;
    setShowProviderFilters(false);
    setProviderDept("");
    setProviderTown("");
    setProviderTipoEntidad("");
    setProviderEsEse("");
    setProviderSearch("");
  }, [selectedPatient]);

  const toggleAllAlarmas = (checked) => {
    setPlan((p) => ({
      ...p,
      ...Object.fromEntries(ALARMA_FIELDS.map((f) => [f.key, checked])),
    }));
  };

  const toggleAllKit = (checked) => {
    setPlan((p) => ({
      ...p,
      ...Object.fromEntries(KIT_FIELDS.map((f) => [f.key, checked])),
    }));
  };

  return (
    <DivContainer>
      <Page>
        <Wrapper>
          <HeaderCard>
            <HeaderTop>
              <Pill>Ruta de Atención Materno-Perinatal</Pill>
              <H1>Plan de Parto</H1>
              <SubTitle>Garantía de atención respetuosa, autónoma y centrada en la familia.</SubTitle>
            </HeaderTop>

            <LegalBox>
              <LegalTitle>Marco Legal de Soporte (Colombia):</LegalTitle>
              <LegalList>
                <li>
                  <strong>Ley 2244 de 2022 (Artículo 03):</strong> Define el Plan de Parto como un diálogo de
                  necesidades, preferencias y expectativas de la mujer frente al proceso de gestación, parto y posparto.
                </li>
                <li>
                  <strong>Resolución 3280 de 2018:</strong> Incorporado en la Ruta Integral de Atención en Salud Materno
                  Perinatal bajo un enfoque de cuidado respetuoso.
                </li>
                <li>
                  Este documento garantiza sus derechos fundamentales a la libertad, autonomía corporal y decisiones
                  reproductivas libres de coerción o violencia, pudiendo modificarse según criterio clínico o voluntad de
                  la gestante entre las semanas 28 y 32.
                </li>
              </LegalList>
            </LegalBox>
          </HeaderCard>

          <Container>
            <FormPanel id="plan-parto-form" onSubmit={submit}>
            <Section>
              <Legend>1. Información General de la Paciente</Legend>
              <SectionTopRow>
                <BtnGeneral bg="blue" padding="6px 14px" radius="6px" fs="12px" fw="500" onClick={() => setSearchOpen(true)}>
                  Buscar paciente
                </BtnGeneral>
                <BtnGeneral bg="gray" padding="6px 14px" radius="6px" fs="12px" fw="500" onClick={() => resetForm(false)} disabled={!selectedPatient}>
                  Nuevo
                </BtnGeneral>
              </SectionTopRow>
              <Grid>
                <Field $span={8}>
                  <Label>Nombre Completo de la Paciente</Label>
                  <Input value={selectedPatient?.name || ""} disabled />
                </Field>
                <Field $span={4}>
                  <Label>Tipo y N° de Documento</Label>
                  <Input value={selectedPatient ? `${selectedPatient.tipo}. ${selectedPatient.id}` : ""} disabled />
                </Field>
                <Field $span={6}>
                  <Label>Dirección de Residencia</Label>
                  <Input
                    value={plan.direccion || ""}
                    disabled
                    placeholder="Calle, Carrera, Barrio o Vereda"
                  />
                </Field>
                <Field $span={3}>
                  <Label>Número de Contacto</Label>
                  <Input
                    value={plan.contacto || ""}
                    disabled
                  />
                </Field>
                <Field $span={3}>
                  <Label>Edad (Años cumplidos)</Label>
                  <Input
                    type="number"
                    min={10}
                    max={60}
                    value={plan.edad ?? ""}
                    disabled
                  />
                </Field>
                <Field $span={4}>
                  <Label>Entidad Promotora de Salud (EPS)</Label>
                  <Input
                    value={plan.eps || ""}
                    disabled
                    placeholder="Nombre de su EPS"
                  />
                </Field>
                <Field $span={4}>
                  <Label>Fecha de Diligenciamiento</Label>
                  <Input
                    type="date"
                    value={plan.fecha_diligenciamiento || ""}
                    onChange={(e) => setPlan((p) => ({ ...p, fecha_diligenciamiento: e.target.value }))}
                  />
                </Field>
              </Grid>

              <Divider />

              <Grid>
                <Field $span={4}>
                  <Label>Semanas de Gestación</Label>
                  <Input
                    type="number"
                    min={0}
                    max={43}
                    value={plan.semanas_gestacion ?? ""}
                    onChange={(e) => setPlan((p) => ({ ...p, semanas_gestacion: e.target.value }))}
                    placeholder="Semanas"
                  />
                </Field>
                <Field $span={4}>
                  <Label>FUM (Última Menstruación)</Label>
                  <Input type="date" value={plan.fum || ""} onChange={(e) => setPlan((p) => ({ ...p, fum: e.target.value }))} />
                </Field>
                <Field $span={4}>
                  <Label>FPP (Probable de Parto)</Label>
                  <Input type="date" value={plan.fpp || ""} onChange={(e) => setPlan((p) => ({ ...p, fpp: e.target.value }))} />
                </Field>
                <Field $span={2}>
                  <Label>Grupo Sang.</Label>
                  <Select value={plan.grupo_sanguineo || ""} onChange={(e) => setPlan((p) => ({ ...p, grupo_sanguineo: e.target.value }))}>
                    <option value="">Sel.</option>
                    <option value="O">O</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="AB">AB</option>
                  </Select>
                </Field>
                <Field $span={2}>
                  <Label>RH</Label>
                  <Select value={plan.rh || ""} onChange={(e) => setPlan((p) => ({ ...p, rh: e.target.value }))}>
                    <option value="">Sel.</option>
                    <option value="+">+</option>
                    <option value="-">-</option>
                  </Select>
                </Field>
                <Field $span={3}>
                  <Label>Peso Actual (Kg)</Label>
                  <Input type="number" step="0.1" value={plan.peso_kg ?? ""} onChange={(e) => setPlan((p) => ({ ...p, peso_kg: e.target.value }))} />
                </Field>
                <Field $span={9}>
                  <Label>Alérgica a:</Label>
                  <Input
                    value={plan.alergias || ""}
                    onChange={(e) => setPlan((p) => ({ ...p, alergias: e.target.value }))}
                    placeholder="Medicamentos, alimentos, látex, etc. (O Ninguna)"
                  />
                </Field>
                <Field $span={6}>
                  <Label>Fecha de Ingreso a Control Prenatal</Label>
                  <Input
                    type="date"
                    value={plan.fecha_ingreso_cpn || ""}
                    onChange={(e) => setPlan((p) => ({ ...p, fecha_ingreso_cpn: e.target.value }))}
                  />
                </Field>
                <Field $span={6}>
                  <Label>N° de Controles Prenatales Realizados</Label>
                  <Input type="number" min={0} value={plan.num_controles ?? ""} onChange={(e) => setPlan((p) => ({ ...p, num_controles: e.target.value }))} />
                </Field>
                <Field $span={6}>
                  <Label>Nombre del Acompañante de Elección</Label>
                  <Input
                    value={plan.acompanante || ""}
                    onChange={(e) => setPlan((p) => ({ ...p, acompanante: e.target.value }))}
                    placeholder="Persona que asistirá al parto"
                  />
                </Field>
                <Field $span={6}>
                  <Label>Número de Contacto Acompañante</Label>
                  <Input
                    value={plan.contacto_acompanante || ""}
                    onChange={(e) => setPlan((p) => ({ ...p, contacto_acompanante: e.target.value }))}
                    placeholder="Teléfono de emergencia"
                  />
                </Field>
              </Grid>
              <HistoryTitle>Histórico</HistoryTitle>
              {loadingHistorico ? (
                <Small>Cargando...</Small>
              ) : (
                <HistoricoList>
                  {historico.length === 0 ? (
                    <Small>No hay registros.</Small>
                  ) : (
                    historico.map((h) => (
                      <HistoricoItem
                        key={h.id_plan_parto}
                        type="button"
                        $active={idPlanParto === h.id_plan_parto}
                        onClick={() => loadDetalle(h.id_plan_parto)}
                        disabled={loadingDetalle}
                      >
                        <StrongInherit>#{h.id_plan_parto}</StrongInherit>
                        <Small>Fecha: {h.fecha_diligenciamiento || "N/A"}</Small>
                      </HistoricoItem>
                    ))
                  )}
                </HistoricoList>
              )}
            </Section>

            <Section>
              <Legend>2. Examen Físico / Hallazgos Clínicos</Legend>
              <SectionDescription>
                Confirmación médica de presentación, posición, situación y descenso fetal (Maniobras de Leopold).
              </SectionDescription>
              <Grid>
                <Field $span={3}>
                  <Label>Presentación Fetal</Label>
                  <RadioColumn>
                    <CheckRow>
                      <input type="radio" checked={plan.presentacion === "CEFALICO"} onChange={() => setPlan((p) => ({ ...p, presentacion: "CEFALICO" }))} />
                      Cefálico
                    </CheckRow>
                    <CheckRow>
                      <input type="radio" checked={plan.presentacion === "PODALICO"} onChange={() => setPlan((p) => ({ ...p, presentacion: "PODALICO" }))} />
                      Podálico / Nalgas
                    </CheckRow>
                  </RadioColumn>
                </Field>
                <Field $span={3}>
                  <Label>Posición Fetal</Label>
                  <RadioColumn>
                    <CheckRow>
                      <input
                        type="radio"
                        checked={plan.posicion === "DORSO_DERECHO"}
                        onChange={() => setPlan((p) => ({ ...p, posicion: "DORSO_DERECHO" }))}
                      />
                      Dorso Derecho
                    </CheckRow>
                    <CheckRow>
                      <input
                        type="radio"
                        checked={plan.posicion === "DORSO_IZQUIERDO"}
                        onChange={() => setPlan((p) => ({ ...p, posicion: "DORSO_IZQUIERDO" }))}
                      />
                      Dorso Izquierdo
                    </CheckRow>
                  </RadioColumn>
                </Field>
                <Field $span={3}>
                  <Label>Situación Fetal</Label>
                  <RadioColumn>
                    <CheckRow>
                      <input
                        type="radio"
                        checked={plan.situacion === "LONGITUDINAL"}
                        onChange={() => setPlan((p) => ({ ...p, situacion: "LONGITUDINAL" }))}
                      />
                      Longitudinal
                    </CheckRow>
                    <CheckRow>
                      <input type="radio" checked={plan.situacion === "TRANSVERSO"} onChange={() => setPlan((p) => ({ ...p, situacion: "TRANSVERSO" }))} />
                      Transverso
                    </CheckRow>
                    <CheckRow>
                      <input type="radio" checked={plan.situacion === "OBLICUO"} onChange={() => setPlan((p) => ({ ...p, situacion: "OBLICUO" }))} />
                      Oblicuo
                    </CheckRow>
                  </RadioColumn>
                </Field>
                <Field $span={3}>
                  <Label>Grado de Descenso</Label>
                  <RadioColumn>
                    <CheckRow>
                      <input
                        type="radio"
                        checked={plan.descenso === "GRADO_-3"}
                        onChange={() => setPlan((p) => ({ ...p, descenso: "GRADO_-3" }))}
                      />
                      Grado -3 (Móvil/Alta)
                    </CheckRow>
                    <CheckRow>
                      <input
                        type="radio"
                        checked={plan.descenso === "GRADO_0"}
                        onChange={() => setPlan((p) => ({ ...p, descenso: "GRADO_0" }))}
                      />
                      Grado 0 (Encajado)
                    </CheckRow>
                    <CheckRow>
                      <input
                        type="radio"
                        checked={plan.descenso === "GRADO_+3"}
                        onChange={() => setPlan((p) => ({ ...p, descenso: "GRADO_+3" }))}
                      />
                      Grado +3 (En Canal)
                    </CheckRow>
                  </RadioColumn>
                </Field>
              </Grid>
            </Section>

            <Section>
              <Legend>3. Asesoría Anticonceptiva Postparto</Legend>
              <SectionDescription>
                Elección del método de planificación familiar de acuerdo con los criterios médicos de elegibilidad.
              </SectionDescription>
              <Grid>
                <Field $span={6}>
                  <CheckRow>
                    <input
                      type="checkbox"
                      checked={!!plan.asesoria_recibida}
                      onChange={(e) => setPlan((p) => ({ ...p, asesoria_recibida: e.target.checked }))}
                    />
                    Asesoría completa sobre todos los métodos anticonceptivos y criterios de elegibilidad específicos para el posparto.
                  </CheckRow>
                </Field>
                <Field $span={6}>
                  <Label>Método de Planificación Elegido</Label>
                  <Select value={plan.metodo_elegido || ""} onChange={(e) => setPlan((p) => ({ ...p, metodo_elegido: e.target.value }))}>
                    <option value="">Seleccione una opción...</option>
                    <option value="POMEROY">Esterilización Quirúrgica (Pomeroy)</option>
                    <option value="IMPLANTE">Implante Subdérmico (3, 4 o 5 años)</option>
                    <option value="DIU">Dispositivo Intrauterino (DIU)</option>
                    <option value="AMPOLLA_TRIMESTRAL">Inyección Trimestral</option>
                    <option value="AMPOLLA_MENSUAL">Inyección Mensual</option>
                    <option value="NO_DESEA">No desea planificar en el momento (Requiere firma de disentimiento)</option>
                  </Select>
                </Field>
              </Grid>
            </Section>

            <Section>
              <Legend>4. Red de Prestadores Asignada</Legend>
              <Grid>
                <Field $span={12}>
                  <CheckRow>
                    <input
                      type="checkbox"
                      checked={showProviderFilters}
                      onChange={() => {
                        const next = !showProviderFilters;
                        setShowProviderFilters(next);
                        setProviderDept("");
                        setProviderTown("");
                        setProviderTipoEntidad("");
                        setProviderEsEse("");
                      }}
                    />
                    Mostrar filtros
                  </CheckRow>
                </Field>

                {showProviderFilters && (
                  <>
                    <Field $span={6}>
                      <Label>Departamento</Label>
                      <Select value={providerDept} onChange={(e) => setProviderDept(e.target.value)}>
                        <option value="">Seleccione</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.descripcion}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field $span={6}>
                      <Label>Municipio</Label>
                      <Select value={providerTown} onChange={(e) => setProviderTown(e.target.value)} disabled={!providerDept}>
                        <option value="">Seleccione</option>
                        {towns.map((t) => (
                          <option key={t.codigo} value={t.codigo}>
                            {t.descripcion}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field $span={6}>
                      <Label>Tipo</Label>
                      <Select value={providerTipoEntidad} onChange={(e) => setProviderTipoEntidad(e.target.value)}>
                        <option value="">Seleccione</option>
                        <option value="4">Pública</option>
                        <option value="1">Privada</option>
                        <option value="3">Mixta</option>
                      </Select>
                    </Field>
                    <Field $span={6}>
                      <Label>ESE</Label>
                      <Select value={providerEsEse} onChange={(e) => setProviderEsEse(e.target.value)}>
                        <option value="">Seleccione</option>
                        <option value="SI">Sí</option>
                        <option value="NO">No</option>
                        <option value="N/A">N/A</option>
                      </Select>
                    </Field>
                  </>
                )}

                <Field $span={6}>
                  <Label>Prestador Asignado para Parto o Cesárea</Label>
                  {isLoadingPrestadores && <Input value="Cargando prestadores..." disabled />}
                  {!isLoadingPrestadores && !!selectedPatient && prestadores.length > 0 && (
                    <ReactSelect
                      value={selectedPrestadorParto}
                      onChange={(opt) => setPlan((p) => ({ ...p, prestador_parto_normal: opt?.value || "" }))}
                      options={prestadoresOptions}
                      placeholder="Buscar prestador..."
                      isClearable
                      isSearchable
                      onInputChange={(txt) => setProviderSearch(txt || "")}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 999999 }),
                        menu: (base) => ({ ...base, zIndex: 999999 }),
                      }}
                      noOptionsMessage={() => "No se encontraron resultados"}
                    />
                  )}
                  {!isLoadingPrestadores && !!selectedPatient && prestadores.length === 0 && <Input value="No se encontraron prestadores" disabled />}
                </Field>
                <Field $span={6}>
                  <Label>Prestador Asignado para Urgencias</Label>
                  {isLoadingPrestadores && <Input value="Cargando prestadores..." disabled />}
                  {!isLoadingPrestadores && !!selectedPatient && prestadores.length > 0 && (
                    <ReactSelect
                      value={selectedPrestadorUrgencias}
                      onChange={(opt) => setPlan((p) => ({ ...p, prestador_urgencias_cesarea: opt?.value || "" }))}
                      options={prestadoresOptions}
                      placeholder="Buscar prestador..."
                      isClearable
                      isSearchable
                      onInputChange={(txt) => setProviderSearch(txt || "")}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 999999 }),
                        menu: (base) => ({ ...base, zIndex: 999999 }),
                      }}
                      noOptionsMessage={() => "No se encontraron resultados"}
                    />
                  )}
                  {!isLoadingPrestadores && !!selectedPatient && prestadores.length === 0 && <Input value="No se encontraron prestadores" disabled />}
                </Field>
              </Grid>
            </Section>

            <Section>
              <Legend>5. Logística, Red de Apoyo y Solicitudes EPS</Legend>
              <Grid>
                <Field $span={6}>
                  <Label>¿Cuenta con un familiar o amigo que apoye el cuidado del hogar durante su estancia hospitalaria?</Label>
                  <RadioRow>
                    <CheckRow>
                      <input type="radio" checked={plan.apoyo_hogar === true} onChange={() => setPlan((p) => ({ ...p, apoyo_hogar: true }))} />
                      Sí
                    </CheckRow>
                    <CheckRow>
                      <input type="radio" checked={plan.apoyo_hogar === false} onChange={() => setPlan((p) => ({ ...p, apoyo_hogar: false }))} />
                      No
                    </CheckRow>
                  </RadioRow>
                </Field>
                <Field $span={6}>
                  <Label>&nbsp;</Label>
                  <Input
                    value={plan.nombre_apoyo_hogar || ""}
                    onChange={(e) => setPlan((p) => ({ ...p, nombre_apoyo_hogar: e.target.value }))}
                    placeholder="¿Quién apoyará?"
                  />
                </Field>

                <Field $span={12}>
                  <Label>Solicitudes específicas requeridas a la EPS:</Label>
                  <Grid>
                    <Field $span={6}>
                      <CheckRow>
                        <input
                          type="checkbox"
                          checked={!!plan.solicitud_eps_traslado_aro}
                          onChange={(e) => setPlan((p) => ({ ...p, solicitud_eps_traslado_aro: e.target.checked }))}
                        />
                        Traslado anticipado por Alto Riesgo Obstétrico (ARO) / Residencia lejana
                      </CheckRow>
                    </Field>
                    <Field $span={6}>
                      <CheckRow>
                        <input
                          type="checkbox"
                          checked={!!plan.solicitud_eps_traslado_cesarea}
                          onChange={(e) => setPlan((p) => ({ ...p, solicitud_eps_traslado_cesarea: e.target.checked }))}
                        />
                        Traslado anticipado por programación de Cesárea
                      </CheckRow>
                    </Field>
                    <Field $span={6}>
                      <CheckRow>
                        <input
                          type="checkbox"
                          checked={!!plan.solicitud_eps_estancia_previa_rural}
                          onChange={(e) => setPlan((p) => ({ ...p, solicitud_eps_estancia_previa_rural: e.target.checked }))}
                        />
                        Estancia hospitalaria previa al parto por residencia en zona rural de difícil acceso
                      </CheckRow>
                    </Field>
                    <Field $span={6}>
                      <CheckRow>
                        <input
                          type="checkbox"
                          checked={!!plan.solicitud_eps_riesgo_zona}
                          onChange={(e) => setPlan((p) => ({ ...p, solicitud_eps_riesgo_zona: e.target.checked }))}
                        />
                        Riesgo crítico identificado de no llegar a tiempo por barreras geográficas
                      </CheckRow>
                    </Field>
                    <Field $span={6}>
                      <CheckRow>
                        <input
                          type="checkbox"
                          checked={!!plan.solicitud_eps_albergue}
                          onChange={(e) => setPlan((p) => ({ ...p, solicitud_eps_albergue: e.target.checked }))}
                        />
                        Necesidad urgente de albergue y/o alojamiento comunitario
                      </CheckRow>
                    </Field>
                    <Field $span={6}>
                      <CheckRow>
                        <input
                          type="checkbox"
                          checked={!!plan.solicitud_eps_acompanante_logistico}
                          onChange={(e) => setPlan((p) => ({ ...p, solicitud_eps_acompanante_logistico: e.target.checked }))}
                        />
                        Requiere cobertura y logística para acompañante permanente
                      </CheckRow>
                    </Field>
                  </Grid>
                </Field>
              </Grid>
            </Section>

            <Section>
              <Legend>6. Requerimientos de Especialidad Quirúrgica u Obstétrica</Legend>
              <Grid>
                <Field $span={8}>
                  <Label>Solicitud de interconsulta para definir vía de parto:</Label>
                  <Input
                    value={plan.interconsulta_especialidad || ""}
                    onChange={(e) => setPlan((p) => ({ ...p, interconsulta_especialidad: e.target.value }))}
                    placeholder="Ej. Perinatología, Anestesiología, Cardiología, etc."
                  />
                </Field>
                <Field $span={4}>
                  <Label>Días de prioridad máxima de asignación</Label>
                  <Input
                    type="number"
                    value={plan.prioridad_dias ?? ""}
                    onChange={(e) => setPlan((p) => ({ ...p, prioridad_dias: e.target.value }))}
                    placeholder="Ej. 3 días"
                  />
                </Field>
              </Grid>
            </Section>

            <Section>
              <Legend>7. Enfoque Intercultural (Médicos Tradicionales y Parteras)</Legend>
              <SectionDescription>
                Conforme a la Resolución 276 de 2019 y Ley 2244 de 2022 para garantizar un parto seguro y respetado.
              </SectionDescription>
              <Grid>
                <Field $span={6}>
                  <Label>¿Solicita atención del parto con Médico Tradicional?</Label>
                  <RadioRow>
                    <CheckRow>
                      <input type="radio" checked={plan.medico_tradicional === true} onChange={() => setPlan((p) => ({ ...p, medico_tradicional: true }))} />
                      Sí
                    </CheckRow>
                    <CheckRow>
                      <input type="radio" checked={plan.medico_tradicional === false} onChange={() => setPlan((p) => ({ ...p, medico_tradicional: false }))} />
                      No
                    </CheckRow>
                  </RadioRow>
                </Field>
                <Field $span={6}>
                  <Label>¿Solicita atención del parto con Partera?</Label>
                  <RadioRow>
                    <CheckRow>
                      <input type="radio" checked={plan.partera === true} onChange={() => setPlan((p) => ({ ...p, partera: true }))} />
                      Sí
                    </CheckRow>
                    <CheckRow>
                      <input type="radio" checked={plan.partera === false} onChange={() => setPlan((p) => ({ ...p, partera: false }))} />
                      No
                    </CheckRow>
                  </RadioRow>
                </Field>

                <Field $span={12}>
                  <AmberFieldset>
                    <AmberLegend>Plan de Seguridad Materno y Neonatal</AmberLegend>
                    <AmberNote>Requisito para mitigar riesgos asistenciales en articulación con la medicina ancestral:</AmberNote>
                    <Spacer />
                    <Grid>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.plan_seguridad_red_urgencias}
                            onChange={(e) => setPlan((p) => ({ ...p, plan_seguridad_red_urgencias: e.target.checked }))}
                          />
                          Se dispone de los contactos directos de la Red Complementaria del Sistema de Salud (Red de Urgencias).
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.plan_seguridad_sistema_referencia}
                            onChange={(e) => setPlan((p) => ({ ...p, plan_seguridad_sistema_referencia: e.target.checked }))}
                          />
                          La paciente conoce el proceso de activación inmediata del sistema de referencia institucional.
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.plan_seguridad_transporte_comunitario}
                            onChange={(e) => setPlan((p) => ({ ...p, plan_seguridad_transporte_comunitario: e.target.checked }))}
                          />
                          Se encuentra coordinada la activación del Sistema de Transporte Comunitario en caso de emergencia.
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.plan_seguridad_identificacion_prestador}
                            onChange={(e) => setPlan((p) => ({ ...p, plan_seguridad_identificacion_prestador: e.target.checked }))}
                          />
                          El Médico Tradicional o Partera ha sido formalmente identificado y articulado por el prestador complementario.
                        </TinyCheckRow>
                      </Field>
                      <Field $span={12}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.plan_seguridad_situaciones_activacion}
                            onChange={(e) => setPlan((p) => ({ ...p, plan_seguridad_situaciones_activacion: e.target.checked }))}
                          />
                          La gestante y su familia fueron informados de manera clara sobre los signos de alarma específicos que activan este plan.
                        </TinyCheckRow>
                      </Field>
                    </Grid>
                  </AmberFieldset>
                </Field>
              </Grid>
            </Section>

            <Section>
              <Legend>8. Valoración Médica para Gestantes de Alto Riesgo (ARO)</Legend>
              <Grid>
                <Field $span={6}>
                  <Label>Impresión Diagnóstica Principal</Label>
                  <TextareaTall
                    value={plan.impresion_diagnostica || ""}
                    onChange={(e) => setPlan((p) => ({ ...p, impresion_diagnostica: e.target.value }))}
                    placeholder="Descripción de los hallazgos patológicos o de riesgo..."
                  />
                </Field>
                <Field $span={6}>
                  <Label>Causales de Clasificación de Alto Riesgo Obstétrico (ARO)</Label>
                  <TextareaTall
                    value={plan.causales_aro || ""}
                    onChange={(e) => setPlan((p) => ({ ...p, causales_aro: e.target.value }))}
                    placeholder="Ej. Preeclampsia, Restricción de Crecimiento Intrauterino (RCIU), Diabetes Gestacional, Edad Materna Extrema, etc."
                  />
                </Field>
                <Field $span={12}>
                  <Label>Vía de Parto Recomendada según Criterio Especializado</Label>
                  <RadioRow>
                    <CheckRow>
                      <input type="radio" checked={plan.via_parto_sugerida === "VAGINAL"} onChange={() => setPlan((p) => ({ ...p, via_parto_sugerida: "VAGINAL" }))} />
                      Vía Vaginal
                    </CheckRow>
                    <CheckRow>
                      <input type="radio" checked={plan.via_parto_sugerida === "CESAREA"} onChange={() => setPlan((p) => ({ ...p, via_parto_sugerida: "CESAREA" }))} />
                      Cesárea Programada
                    </CheckRow>
                  </RadioRow>
                </Field>
              </Grid>
            </Section>

            <Section>
              <Legend>9. Educación en Salud y Preparación Hospitalaria</Legend>
              <Grid>
                <Field $span={12}>
                  <BlockSubtitle>Se brindó información detallada y la paciente comprende:</BlockSubtitle>
                </Field>
                <Field $span={6}>
                  <InnerFieldset>
                    <RedUpper>Signos de alarma (Urgencia Inmediata):</RedUpper>
                    <Spacer $h="8px" />
                    <SelectAllRow>
                      <input type="checkbox" checked={allAlarmasChecked} onChange={(e) => toggleAllAlarmas(e.target.checked)} />
                      Seleccionar todos
                    </SelectAllRow>
                    {ALARMA_FIELDS.map((f) => (
                      <TinyCheckRow key={f.key}>
                        <input
                          type="checkbox"
                          checked={!!plan[f.key]}
                          onChange={(e) => setPlan((p) => ({ ...p, [f.key]: e.target.checked }))}
                        />
                        {f.label}
                      </TinyCheckRow>
                    ))}
                  </InnerFieldset>
                </Field>

                <Field $span={6}>
                  <InnerFieldsetSolid>
                    <InnerLegend>Elementos requeridos el día del ingreso:</InnerLegend>
                    <Spacer />
                    <SelectAllRow>
                      <input type="checkbox" checked={allKitChecked} onChange={(e) => toggleAllKit(e.target.checked)} />
                      Seleccionar todos
                    </SelectAllRow>
                    {KIT_FIELDS.map((f) => (
                      <TinyCheckRow key={f.key}>
                        <input
                          type="checkbox"
                          checked={!!plan[f.key]}
                          onChange={(e) => setPlan((p) => ({ ...p, [f.key]: e.target.checked }))}
                        />
                        {f.label}
                      </TinyCheckRow>
                    ))}
                  </InnerFieldsetSolid>
                </Field>
              </Grid>
            </Section>

            <Section>
              <Legend>10. Preferencias de la Madre para el Trabajo de Parto</Legend>
              <SectionDescription>
                Sujetas a viabilidad médica, evolución clínica intraparto y políticas institucionales del prestador.
              </SectionDescription>
              <Grid>
                <Field $span={6}>
                  <InnerFieldset>
                    <InnerLegend>Preferencia de Entorno / Ambiente</InnerLegend>
                    <Spacer />
                    <TinyCheckRow>
                      <input
                        type="checkbox"
                        checked={!!plan.pref_ambiente_luz_tenue}
                        onChange={(e) => setPlan((p) => ({ ...p, pref_ambiente_luz_tenue: e.target.checked }))}
                      />
                      Espacio tranquilo y luz tenue
                    </TinyCheckRow>
                    <TinyCheckRow>
                      <input
                        type="checkbox"
                        checked={!!plan.pref_ambiente_musica}
                        onChange={(e) => setPlan((p) => ({ ...p, pref_ambiente_musica: e.target.checked }))}
                      />
                      Permitir música suave propia
                    </TinyCheckRow>
                    <TinyCheckRow>
                      <input
                        type="checkbox"
                        checked={!!plan.pref_ambiente_privacidad}
                        onChange={(e) => setPlan((p) => ({ ...p, pref_ambiente_privacidad: e.target.checked }))}
                      />
                      Respetar privacidad máxima (evitar personal innecesario)
                    </TinyCheckRow>
                  </InnerFieldset>

                  <Spacer />

                  <InnerFieldset>
                    <InnerLegend>Acompañamiento</InnerLegend>
                    <Spacer />
                    <TinyCheckRow>
                      <input
                        type="checkbox"
                        checked={!!plan.pref_acompanamiento_continuo}
                        onChange={(e) => setPlan((p) => ({ ...p, pref_acompanamiento_continuo: e.target.checked }))}
                      />
                      Deseo acompañamiento continuo de mi red de apoyo en el parto
                    </TinyCheckRow>
                    <Spacer $h="8px" />
                    <WhiteCompactInput
                      value={plan.personas_acompanantes_sala || ""}
                      onChange={(e) => setPlan((p) => ({ ...p, personas_acompanantes_sala: e.target.value }))}
                      placeholder="Nombre(s) de quiénes desea que estén presentes"
                    />
                  </InnerFieldset>
                </Field>

                <Field $span={6}>
                  <InnerFieldset>
                    <InnerLegend>Movilidad y Posición en Trabajo de Parto</InnerLegend>
                    <Spacer />
                    <TinyCheckRow>
                      <input
                        type="checkbox"
                        checked={!!plan.libertad_movimiento}
                        onChange={(e) => setPlan((p) => ({ ...p, libertad_movimiento: e.target.checked }))}
                      />
                      Deseo total libertad de movimiento durante el trabajo de parto
                    </TinyCheckRow>
                  </InnerFieldset>
                </Field>

                <Field $span={12}>
                  <InnerFieldset>
                    <InnerLegend>Expectativas frente a Intervenciones Clínicas</InnerLegend>
                    <Spacer />
                    <Grid>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_intervenciones_consentimiento}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_intervenciones_consentimiento: e.target.checked }))}
                          />
                          Intervenciones solo bajo indicación médica explicada y consentimiento informado.
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_intervenciones_tactos}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_intervenciones_tactos: e.target.checked }))}
                          />
                          Evitar tacto vaginal rutinario repetitivo a menos que sea estrictamente necesario.
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_intervenciones_episiotomia}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_intervenciones_episiotomia: e.target.checked }))}
                          />
                          Evitar la realización de episiotomía rutinaria profiláctica.
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_intervenciones_oxitocina}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_intervenciones_oxitocina: e.target.checked }))}
                          />
                          Evitar inducción artificial con oxitocina sintética si la dilatación progresa bien.
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_intervenciones_rasurado}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_intervenciones_rasurado: e.target.checked }))}
                          />
                          Evitar el rasurado perineal sistemático en la admisión.
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_intervenciones_kristeller}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_intervenciones_kristeller: e.target.checked }))}
                          />
                          <strong>Restricción Absoluta:</strong> Evitar la maniobra de Kristeller (presión fúndica abdominal).
                        </TinyCheckRow>
                      </Field>
                    </Grid>
                  </InnerFieldset>
                </Field>

                <Field $span={12}>
                  <InnerFieldset>
                    <InnerLegend>Preferencia para el Manejo del Dolor (Analgesia)</InnerLegend>
                    <Spacer />
                    <Grid>
                      <Field $span={4}>
                        <TinyCheckRow>
                          <input
                            type="radio"
                            checked={plan.pref_analgesia === "AINES"}
                            onChange={() => setPlan((p) => ({ ...p, pref_analgesia: "AINES" }))}
                          />
                          No narcóticos / Manejo con AINES
                        </TinyCheckRow>
                      </Field>
                      <Field $span={4}>
                        <TinyCheckRow>
                          <input
                            type="radio"
                            checked={plan.pref_analgesia === "NARCOTICOS"}
                            onChange={() => setPlan((p) => ({ ...p, pref_analgesia: "NARCOTICOS" }))}
                          />
                          Analgesia Sistémica / Narcóticos
                        </TinyCheckRow>
                      </Field>
                      <Field $span={4}>
                        <TinyCheckRow>
                          <input
                            type="radio"
                            checked={plan.pref_analgesia === "NEURO_AXIAL"}
                            onChange={() => setPlan((p) => ({ ...p, pref_analgesia: "NEURO_AXIAL" }))}
                          />
                          Analgesia Neuroaxial (Epidural Temprana)
                        </TinyCheckRow>
                      </Field>
                    </Grid>
                    <TinyNote>
                      Contraindicaciones absolutas para epidural: Coagulopatías, infección local/sistémica, hipovolemia crítica no corregida o rechazo expreso de la madre.
                    </TinyNote>
                  </InnerFieldset>
                </Field>

                <Field $span={12}>
                  <InnerFieldset>
                    <InnerLegend>Nacimiento y Recepción del Recién Nacido (RN)</InnerLegend>
                    <Spacer />
                    <Grid>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_rn_contacto_piel}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_rn_contacto_piel: e.target.checked }))}
                          />
                          Contacto piel a piel inmediato (Mínimo la primera hora si no hay complicación)
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_rn_lactancia_precoz}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_rn_lactancia_precoz: e.target.checked }))}
                          />
                          Inicio de la lactancia materna exclusiva dentro de la primera hora de vida
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_rn_pinzamiento_retrasado}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_rn_pinzamiento_retrasado: e.target.checked }))}
                          />
                          Retrasar el pinzamiento del cordón umbilical (Pinzamiento fisiológico habitual)
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_rn_corte_acompanante}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_rn_corte_acompanante: e.target.checked }))}
                          />
                          Deseo que mi acompañante realice el corte guiado del cordón umbilical
                        </TinyCheckRow>
                      </Field>
                      <Field $span={12}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_rn_entrega_placenta}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_rn_entrega_placenta: e.target.checked }))}
                          />
                          Por convicciones socioculturales o prácticas personales, solicito formalmente la entrega de la placenta.
                        </TinyCheckRow>
                      </Field>
                    </Grid>
                  </InnerFieldset>
                </Field>

                <Field $span={12}>
                  <InnerFieldset>
                    <InnerLegend>Consideraciones en Caso de Cesárea o Complicación</InnerLegend>
                    <Spacer />
                    <Grid>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_cesarea_acompanante_quirofano}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_cesarea_acompanante_quirofano: e.target.checked }))}
                          />
                          En cesárea programada: Deseo que mi acompañante esté presente en quirófano
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_cesarea_contacto_cesarea}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_cesarea_contacto_cesarea: e.target.checked }))}
                          />
                          En cesárea: Contacto piel a piel en el quirófano en cuanto la estabilidad lo permita
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_postparto_alojamiento_conjunto}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_postparto_alojamiento_conjunto: e.target.checked }))}
                          />
                          Postparto habitual: Alojamiento conjunto madre e hijo permanente de 24 horas
                        </TinyCheckRow>
                      </Field>
                      <Field $span={6}>
                        <TinyCheckRow>
                          <input
                            type="checkbox"
                            checked={!!plan.pref_postparto_vigilancia_signos}
                            onChange={(e) => setPlan((p) => ({ ...p, pref_postparto_vigilancia_signos: e.target.checked }))}
                          />
                          Vigilancia y monitoreo estricto de mis signos vitales, sangrado e involución uterina
                        </TinyCheckRow>
                      </Field>
                    </Grid>
                  </InnerFieldset>
                </Field>
              </Grid>
            </Section>

            <IndigoSection>
              <Legend>11. Control de Trámite Administrativo y Respuesta de la EPS</Legend>
              <Grid>
                <Field $span={4}>
                  <IndigoLabel>EPS que tramita la solicitud</IndigoLabel>
                  <IndigoInput value={epsTramite.eps_tramite || ""} onChange={(e) => setEpsTramite((p) => ({ ...p, eps_tramite: e.target.value }))} />
                </Field>
                <Field $span={4}>
                  <IndigoLabel>Fecha de Socialización del Plan</IndigoLabel>
                  <IndigoInput
                    type="date"
                    value={epsTramite.fecha_socializacion || ""}
                    onChange={(e) => setEpsTramite((p) => ({ ...p, fecha_socializacion: e.target.value }))} />
                </Field>
                <Field $span={4}>
                  <IndigoLabel>Profesional de la EPS que recibe / audita</IndigoLabel>
                  <IndigoInput
                    value={epsTramite.profesional_eps || ""}
                    onChange={(e) => setEpsTramite((p) => ({ ...p, profesional_eps: e.target.value }))}
                    placeholder="Nombre del funcionario u auditor"
                  />
                </Field>
                <Field $span={4}>
                  <IndigoLabel>Fecha de Respuesta del Trámite</IndigoLabel>
                  <IndigoInput type="date" value={epsTramite.fecha_respuesta || ""} onChange={(e) => setEpsTramite((p) => ({ ...p, fecha_respuesta: e.target.value }))} />
                </Field>
                <Field $span={4}>
                  <IndigoLabel>Tiempo transcurrido (Días Calendario)</IndigoLabel>
                  <IndigoInput
                    type="number"
                    value={epsTramite.dias_transcurridos ?? ""}
                    onChange={(e) => setEpsTramite((p) => ({ ...p, dias_transcurridos: e.target.value }))}
                    placeholder="Total días"
                  />
                </Field>
                <IndigoFieldset>
                  <IndigoLegend>Documentación Anexa Obligatoria Indexada:</IndigoLegend>
                  <Grid>
                    <Field $span={4}>
                      <IndigoCheckRow>
                        <input
                          type="checkbox"
                          checked={!!epsTramite.anexo_plan_parto_discutido}
                          onChange={(e) => setEpsTramite((p) => ({ ...p, anexo_plan_parto_discutido: e.target.checked }))}
                        />
                        Plan de Parto Discutido
                      </IndigoCheckRow>
                    </Field>
                    <Field $span={4}>
                      <IndigoCheckRow>
                        <input
                          type="checkbox"
                          checked={!!epsTramite.anexo_resumen_hc}
                          onChange={(e) => setEpsTramite((p) => ({ ...p, anexo_resumen_hc: e.target.checked }))}
                        />
                        Resumen de Historia Clínica
                      </IndigoCheckRow>
                    </Field>
                    <Field $span={4}>
                      <IndigoCheckRow>
                        <input
                          type="checkbox"
                          checked={!!epsTramite.anexo_carnet_clap}
                          onChange={(e) => setEpsTramite((p) => ({ ...p, anexo_carnet_clap: e.target.checked }))}
                        />
                        Carné Materno / Formato CLAP
                      </IndigoCheckRow>
                    </Field>
                  </Grid>
                </IndigoFieldset>
              </Grid>
            </IndigoSection>

            <FooterActions>
              <BtnGeneral bg="cyan" padding="7px 16px" radius="6px" fs="13px" fw="500" onClick={() => setPrintOpen(true)} disabled={!idPlanParto}>
                Imprimir
              </BtnGeneral>
              <BtnGeneral type="submit" bg="green" padding="7px 18px" radius="6px" fs="13px" fw="600" disabled={!canSave || saving}>
                {idPlanParto ? "Guardar cambios" : "Guardar Datos del Formulario"}
              </BtnGeneral>
            </FooterActions>
          </FormPanel>
          </Container>
        </Wrapper>

        <SearchModal
          isOpen={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelect={handleSelectPatient}
          patientFilters={searchPatientFilters}
        />
        <Modal
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title={`Plan de Parto ${idPlanParto ? `#${idPlanParto}` : ""}`}
          width="95vw"
          height="90vh"
          maxH="90vh"
          minH="90vh"
          padding="0"
          closeOnOverlay={false}
        >
          <PdfPreview title={`Plan de Parto ${idPlanParto ? `#${idPlanParto}` : ""}`} reportName="planParto" loadData={loadPrintData} meta={printMeta} />
        </Modal>
      </Page>
    </DivContainer>
  );
};

export default PlanParto;
